import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import useFirebaseNotifications from '../../hooks/useFirebaseNotifications';
import useAuth from '../../hooks/useAuth';

/**
 * Component to prompt users to enable notifications
 * Shows when notifications are not enabled
 */
const NotificationPrompt = () => {
  const { auth } = useAuth();
  const userId = auth?.employee?._id || auth?.employee?.id;
  const { requestPermission, notificationPermission, initializeNotifications } = useFirebaseNotifications(userId);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Show prompt if notifications are not granted and not dismissed
    const dismissed = localStorage.getItem('notificationPromptDismissed') === 'true';
    setIsDismissed(dismissed);
    
    if (notificationPermission === 'default' && !dismissed && userId) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [notificationPermission, userId]);

  const handleEnable = async () => {
    const granted = await requestPermission();
    if (granted) {
      await initializeNotifications();
      setIsVisible(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    localStorage.setItem('notificationPromptDismissed', 'true');
  };

  if (!isVisible || isDismissed) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] max-w-sm">
      <div className="bg-white dark:bg-dark-100 border border-gray-200 dark:border-dark-200 rounded-lg shadow-lg p-4 flex items-start gap-3">
        <div className="flex-shrink-0">
          <Bell className="w-5 h-5 text-primary-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-light-100 mb-1">
            Enable Notifications
          </h3>
          <p className="text-xs text-gray-600 dark:text-light-400 mb-3">
            Get notified about new messages and important updates.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleEnable}
              className="px-3 py-1.5 text-xs font-medium bg-primary-accent text-white rounded-md hover:bg-primary-accent/90 transition-colors"
            >
              Enable
            </button>
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-light-400 hover:bg-gray-100 dark:hover:bg-dark-200 rounded-md transition-colors"
            >
              Maybe Later
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 hover:bg-gray-100 dark:hover:bg-dark-200 rounded transition-colors"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>
    </div>
  );
};

export default NotificationPrompt;
